/**
 * EVO — 1C / Excel import ma'lumot qatlami.
 *
 * Tiplar `apps/api/src/modules/import/import.service.ts` dagi javob
 * interfeyslaridan MAYDONMA-MAYDON ko'chirilgan.
 *
 * ============================================================================
 * TO'RT BOSQICH — VA NIMA UCHUN AYNAN TO'RTTA
 * ============================================================================
 * GAP_ANALYSIS §C5: real kelayotgan fayllarda sarlavha 3-qatorda, raqamlar
 * matn, bir katakda «100 dona» yozilgan. «Faylni tanladingiz — import qilindi»
 * degan bitta tugma bunday fayl bilan ishlamaydi: u yo yiqiladi, yo eng
 * yomoni — 4 000 qatorni jimgina noto'g'ri kiritadi.
 *
 *   1. YUKLASH   `POST /api/import/batches?entityType=…` (multipart)
 *                → varaqlar, aniqlangan sarlavha qatori, AVTOMATIK TAKLIF
 *   2. MOSLASH   `POST /api/import/batches/:id/mapping`
 *                → 5 qatorli natija ko'rinishi (preview)
 *   3. TEKSHIRISH `POST /api/import/batches/:id/validate` — IDEMPOTENT
 *                → xatolar va OGOHLANTIRISHLAR
 *   4. KIRITISH  `POST /api/import/batches/:id/commit` — bitta tranzaksiya
 *                → keyin `rollback` (sabab MAJBURIY)
 *
 * ============================================================================
 * BARCHA ENDPOINTLAR — `migration.run`, O'QISH HAM
 * ============================================================================
 * `migration.view` degan alohida ruxsat YO'Q (import.controller.ts izohi), va
 * partiya qatorlari mijoz telefoni va manzilini o'z ichiga oladi. Jonli
 * tekshirilgan: `migration.run` faqat adminda — moliyachi, omborchi va
 * sotuvchi `GET /api/import/batches` da 403 oladi.
 */
import { api, tokenStore } from './api';
import type { Page } from './products';

/**
 * `mappers/mapper.types.ts` → `IMPORT_ENTITY_TYPES`.
 *
 * TARTIB MA'NOLI va u ekranda shu holicha ko'rinadi: ochilish qoldig'i
 * master data'ga TAYANADI (qoldiqda mahsulot, qarzda kontragent
 * ko'rsatiladi). Tanlagichdagi ketma-ketlik ko'chirish tartibini
 * o'rgatadi — operator ro'yxatni yuqoridan pastga bajaradi.
 */
export const IMPORT_ENTITY_TYPES = [
  'product',
  'customer',
  'supplier',
  'stock',
  'ar',
  'ap',
  'price',
] as const;
export type ImportEntityType = (typeof IMPORT_ENTITY_TYPES)[number];

/**
 * Ochilish qoldig'i turlari — master data emas, HARAKAT.
 *
 * Serverdagi `OPENING_ENTITY_TYPES` ning nusxasi (`IMPORT_ENTITY_TYPES` bilan
 * bir xil sabab: web va api alohida paket va bu ro'yxat uchun umumiy runtime
 * import yo'q). Ekranda u ogohlantirishni faqat shu turlarda ko'rsatish
 * uchun kerak.
 */
const OPENING_ENTITY_TYPES: readonly ImportEntityType[] = ['stock', 'ar', 'ap', 'price'];

export function isOpeningEntity(value: ImportEntityType): boolean {
  return OPENING_ENTITY_TYPES.includes(value);
}

/** `migration.ts` → `MIGRATION_BATCH_STATUS`. */
export const BATCH_STATUS_VALUES = [
  'uploaded',
  'validated',
  'dry_run',
  'committed',
  'rolled_back',
] as const;
export type BatchStatus = (typeof BATCH_STATUS_VALUES)[number];

/** `migration.ts` → `MIGRATION_ROW_STATUS`. */
export const ROW_STATUS_VALUES = ['pending', 'valid', 'error', 'imported', 'skipped'] as const;
export type RowStatus = (typeof ROW_STATUS_VALUES)[number];

export interface SheetPreview {
  readonly name: string;
  /** Bo'sh bo'lmagan qatorlar soni (sarlavhadan yuqorisi bilan birga). */
  readonly rowCount: number;
  readonly headerRow: number;
  /** Ustun HARFI → sarlavha matni. */
  readonly columns: Readonly<Record<string, string>>;
}

/** EVO maydoni haqidagi ma'lumot — moslash ekrani shu ro'yxatdan quriladi. */
export interface FieldSpec {
  readonly key: string;
  readonly labelUz: string;
  readonly required: boolean;
  readonly example: string;
  readonly aliases: readonly string[];
  readonly hint?: string;
}

export interface BatchSummary {
  readonly id: number;
  readonly entityType: string;
  readonly fileName: string | null;
  readonly status: string;
  readonly totalRows: number;
  readonly validRows: number;
  readonly errorRows: number;
  readonly startedAt: string | null;
  readonly finishedAt: string | null;
  readonly notes: string | null;
  readonly createdAt: string;
}

export interface UploadResponse extends BatchSummary {
  readonly sheets: readonly SheetPreview[];
  readonly selectedSheet: string;
  readonly headerRow: number;
  readonly columns: Readonly<Record<string, string>>;
  /** Avtomatik taklif: EVO maydoni → ustun HARFI. Operator tasdiqlaydi yoki o'zgartiradi. */
  readonly suggestedMapping: Readonly<Record<string, string>>;
  readonly fields: readonly FieldSpec[];
}

export interface MappingResponse extends BatchSummary {
  readonly headerRow: number;
  readonly mapping: Readonly<Record<string, string>>;
  readonly defaults: Readonly<Record<string, string>>;
  /** Moslashdan keyingi birinchi qatorlar — operator natijani KO'RADI. */
  readonly preview: readonly {
    readonly rowNumber: number;
    readonly values: Readonly<Record<string, string>>;
  }[];
  readonly skippedRows: number;
}

export interface ValidationResponse extends BatchSummary {
  /** `status = 'valid'`, lekin `errorMessage` to'lgan qatorlar soni. */
  readonly warningRows: number;
  /** Eng ko'p uchragan xatolar — operator nimadan boshlashni biladi. */
  readonly topErrors: readonly { readonly message: string; readonly count: number }[];
}

export interface RowResponse {
  readonly id: number;
  readonly rowNumber: number;
  readonly status: string;
  /**
   * XATO YOKI OGOHLANTIRISH — bitta ustun, ikki xil ma'no.
   *
   * `status = 'error'` bo'lsa bu xato; `status = 'valid'` bo'lib turib
   * `errorMessage` to'lgan bo'lsa — bu OGOHLANTIRISH va u
   * `Ogohlantirish:` bilan boshlanadi (`WARNING_PREFIX`).
   */
  readonly errorMessage: string | null;
  readonly rawData: Readonly<Record<string, unknown>>;
  readonly mappedData: Readonly<Record<string, unknown>> | null;
  readonly targetTable: string | null;
  readonly targetId: number | null;
}

/**
 * Server qo'ygan ogohlantirish prefiksi (`import.service.ts` → `WARNING_PREFIX`).
 *
 * Yaqin dublikat («Люстра 60W» va «Люстра 80W») — XATO EMAS: ikkalasi ham
 * haqiqiy mahsulot va ikkalasi ham kirishi kerak. Ularni xato sifatida
 * ko'rsatish operatorni to'xtatib, haqiqiy xatolarni ko'mib yuboradi.
 */
export const WARNING_PREFIX = 'Ogohlantirish:';

export function isWarningRow(row: RowResponse): boolean {
  return row.status === 'valid' && row.errorMessage !== null;
}

/** Prefiksni olib tashlaydi — ekranda u alohida belgi bilan ko'rsatiladi. */
export function warningText(message: string): string {
  return message.startsWith(WARNING_PREFIX)
    ? message.slice(WARNING_PREFIX.length).trim()
    : message;
}

// ---------------------------------------------------------------------------
// 1-BOSQICH — yuklash
// ---------------------------------------------------------------------------

/**
 * Faylni yuboradi.
 *
 * `FormData` ishlatilgani uchun `Content-Type` QO'LDA QO'YILMAYDI: brauzer
 * `multipart/form-data; boundary=…` ni o'zi yozadi va boundary'siz sarlavha
 * so'rovni serverda o'qib bo'lmaydigan qiladi. `api.rawBody` aynan shu holat
 * uchun bor (`lib/api.ts`).
 *
 * `entityType` QUERY da, fayl ichida emas: bitta fayl BITTA entity turiga
 * tegishli va turni sarlavhalarga qarab taxmin qilish mumkin emas — mijozlar
 * ro'yxati «Kod | Nomi» bilan ta'minotchilar ro'yxatidan farq qilmaydi.
 */
export function uploadImportFile(
  entityType: ImportEntityType,
  file: File,
  sheet?: string,
): Promise<UploadResponse> {
  const form = new FormData();
  form.append('file', file, file.name);

  return api.post<UploadResponse>('/import/batches', undefined, {
    rawBody: form,
    query: { entityType, sheet },
  });
}

// ---------------------------------------------------------------------------
// 2-BOSQICH — moslash
// ---------------------------------------------------------------------------

export interface MappingInput {
  /** Avtomatik aniqlash — TAXMIN, va u xato qilishi mumkin. */
  readonly headerRow?: number;
  /** EVO maydoni → fayl ustuni (HARF yoki sarlavha MATNI — server ikkalasini tushunadi). */
  readonly columns: Readonly<Record<string, string>>;
  /** Butun ustunga tegishli SOBIT qiymat (fayl ustunisiz maydon uchun). */
  readonly defaults?: Readonly<Record<string, string>>;
}

export function setImportMapping(batchId: number, input: MappingInput): Promise<MappingResponse> {
  return api.post<MappingResponse>(`/import/batches/${batchId}/mapping`, {
    ...(input.headerRow === undefined ? {} : { headerRow: input.headerRow }),
    columns: input.columns,
    ...(input.defaults && Object.keys(input.defaults).length > 0
      ? { defaults: input.defaults }
      : {}),
  });
}

// ---------------------------------------------------------------------------
// 3-BOSQICH — tekshirish
// ---------------------------------------------------------------------------

/** IDEMPOTENT: xohlagancha marta chaqirilaveradi. */
export function validateImport(batchId: number): Promise<ValidationResponse> {
  return api.post<ValidationResponse>(`/import/batches/${batchId}/validate`);
}

export interface RowListParams {
  readonly page: number;
  readonly pageSize: number;
  readonly status?: RowStatus | undefined;
  readonly warningsOnly?: boolean | undefined;
}

export function fetchImportRows(
  batchId: number,
  params: RowListParams,
  signal?: AbortSignal,
): Promise<Page<RowResponse>> {
  return api.get<Page<RowResponse>>(`/import/batches/${batchId}/rows`, {
    query: {
      page: params.page,
      pageSize: params.pageSize,
      status: params.status,
      warningsOnly: params.warningsOnly === true ? 'true' : undefined,
    },
    ...(signal ? { signal } : {}),
  });
}

// ---------------------------------------------------------------------------
// 4-BOSQICH — kiritish va qaytarish
// ---------------------------------------------------------------------------

export function commitImport(batchId: number): Promise<BatchSummary> {
  return api.post<BatchSummary>(`/import/batches/${batchId}/commit`);
}

/**
 * Sabab MAJBURIY: commit qilingan partiyani qaytarish — bu bazadan yozuv
 * O'CHIRISH, ya'ni «master data hech qachon DELETE qilinmaydi» qoidasidan
 * yagona istisno (DATA_MODEL §1.2).
 */
export function rollbackImport(batchId: number, reason: string): Promise<BatchSummary> {
  return api.post<BatchSummary>(`/import/batches/${batchId}/rollback`, { reason });
}

// ---------------------------------------------------------------------------
// O'qish
// ---------------------------------------------------------------------------

export interface BatchListParams {
  readonly page: number;
  readonly pageSize: number;
  readonly entityType?: ImportEntityType | undefined;
  readonly status?: BatchStatus | undefined;
}

export function fetchImportBatches(
  params: BatchListParams,
  signal?: AbortSignal,
): Promise<Page<BatchSummary>> {
  return api.get<Page<BatchSummary>>('/import/batches', {
    query: {
      page: params.page,
      pageSize: params.pageSize,
      sortBy: 'createdAt',
      sortDir: 'desc',
      entityType: params.entityType,
      status: params.status,
    },
    ...(signal ? { signal } : {}),
  });
}

export function fetchImportBatch(batchId: number, signal?: AbortSignal): Promise<UploadResponse> {
  return api.get<UploadResponse>(`/import/batches/${batchId}`, { ...(signal ? { signal } : {}) });
}

// ---------------------------------------------------------------------------
// Shablon
// ---------------------------------------------------------------------------

/**
 * To'g'ri sarlavhali `.xlsx` shablonni yuklab oladi.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ NIMA UCHUN ODDIY `<a href>` EMAS                                         │
 * │                                                                          │
 * │ Endpoint `migration.run` talab qiladi, ya'ni `Authorization` sarlavhasi  │
 * │ kerak. Brauzer oddiy havolada uni yubormaydi (access token XOTIRADA,     │
 * │ cookie'da emas — `lib/api.ts` boshidagi siyosat), shuning uchun havola   │
 * │ 401 beradi va operator «shablon buzilgan» deb o'ylaydi.                  │
 * │                                                                          │
 * │ Shuning uchun fayl `fetch` bilan olinadi va `blob:` havolasi orqali      │
 * │ saqlanadi — `lib/export-csv.ts` dagi bilan bir xil naqsh.                │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
export async function downloadImportTemplate(entityType: ImportEntityType): Promise<void> {
  const base = import.meta.env.VITE_API_BASE_URL ?? '/api';
  const token = tokenStore.getAccessToken();

  const response = await fetch(`${base}/import/templates/${entityType}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    throw new Error(String(response.status));
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `evo-${entityType}-shablon.xlsx`;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export const importKeys = {
  all: ['import'] as const,
  batches: (params: BatchListParams) => ['import', 'batches', params] as const,
  batch: (id: number) => ['import', 'batch', id] as const,
  rows: (id: number, params: RowListParams) => ['import', 'rows', id, params] as const,
};
